import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Tabs, 
  Steps, 
  Button, 
  Space, 
  Radio, 
  Checkbox, 
  Input, 
  Divider,
  Tag,
  Result,
  Modal,
  message,
  Alert,
  Row,
  Col,
  Tooltip,
  Statistic
} from 'antd';
import { 
  LeftOutlined, 
  RightOutlined, 
  CheckOutlined, 
  ClockCircleOutlined,
  FileTextOutlined,
  CodeOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import CodeEditor from '../../components/CodeEditor';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Step } = Steps;
const { TextArea } = Input;

const ExerciseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exercise, setExercise] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [exerciseSubmitted, setExerciseSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  // 模拟获取练习数据
  useEffect(() => {
    const fetchExercise = () => {
      setTimeout(() => {
        const mockExercise = {
          id: parseInt(id),
          title: '循环结构基础练习',
          description: '本练习主要测试对循环结构的理解和应用能力，包括for循环和while循环的使用场景。',
          timeLimit: 60, // 分钟
          totalPoints: 100,
          passingPoints: 60,
          questions: [
            {
              id: 1,
              type: 'multiple_choice',
              title: '下列哪个循环结构适合在不知道循环次数的情况下使用？',
              options: [
                { id: 'A', text: 'for循环' },
                { id: 'B', text: 'while循环' },
                { id: 'C', text: 'do-while循环' },
                { id: 'D', text: '以上都不是' }
              ],
              answer: 'B',
              points: 10
            },
            {
              id: 2,
              type: 'multiple_select',
              title: '以下哪些是JavaScript中的循环结构？（多选）',
              options: [
                { id: 'A', text: 'for循环' },
                { id: 'B', text: 'while循环' },
                { id: 'C', text: 'until循环' },
                { id: 'D', text: 'forEach方法' }
              ],
              answer: ['A', 'B', 'D'],
              points: 15
            },
            {
              id: 3,
              type: 'coding',
              title: '使用循环计算1到100的和',
              description: '请编写一个函数，使用循环结构计算从1到100的所有整数之和，并返回结果。',
              initialCode: '// 计算1到100的和\nfunction calculateSum() {\n  // 编写代码\n  \n  // 返回结果\n  return sum;\n}\n',
              language: 'javascript',
              testCases: [
                { input: [], output: 5050 }
              ],
              points: 25
            },
            {
              id: 4,
              type: 'coding',
              title: '使用嵌套循环打印直角三角形',
              description: '请编写一个函数，接收一个正整数n作为参数，使用嵌套循环打印一个由"*"组成的直角三角形，高度为n行。',
              initialCode: '// 打印直角三角形\nfunction printTriangle(n) {\n  // 编写代码\n  \n}\n',
              language: 'javascript',
              testCases: [
                { input: [3], output: '*\\n**\\n***\\n' },
                { input: [5], output: '*\\n**\\n***\\n****\\n*****\\n' }
              ],
              points: 50
            }
          ]
        };

        setExercise(mockExercise);
        setTimeLeft(mockExercise.timeLimit * 60); // 转换为秒
        
        // 初始化答案结构
        const initialAnswers = {};
        mockExercise.questions.forEach(q => {
          if (q.type === 'multiple_choice') {
            initialAnswers[q.id] = null;
          } else if (q.type === 'multiple_select') {
            initialAnswers[q.id] = [];
          } else if (q.type === 'coding') {
            initialAnswers[q.id] = q.initialCode;
          }
        });
        setAnswers(initialAnswers);
        
        setLoading(false);
      }, 1000);
    };

    fetchExercise();
  }, [id]);

  // 倒计时
  useEffect(() => {
    if (!loading && timeLeft > 0 && !exerciseSubmitted) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            submitExercise();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [loading, timeLeft, exerciseSubmitted]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleMultipleSelectChange = (questionId, optionId, checked) => {
    setAnswers(prev => {
      const currentAnswers = [...(prev[questionId] || [])];
      
      if (checked && !currentAnswers.includes(optionId)) {
        currentAnswers.push(optionId);
      } else if (!checked && currentAnswers.includes(optionId)) {
        const index = currentAnswers.indexOf(optionId);
        currentAnswers.splice(index, 1);
      }
      
      return {
        ...prev,
        [questionId]: currentAnswers
      };
    });
  };

  const handleCodeChange = (questionId, code) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: code
    }));
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < exercise.questions.length) {
      setCurrentQuestion(index);
      setActiveTab('description');
    }
  };

  const submitExercise = () => {
    setIsSubmitting(true);
    
    // 模拟提交并评分
    setTimeout(() => {
      // 模拟评分逻辑
      let totalScore = 0;
      let correctAnswers = 0;
      
      exercise.questions.forEach(question => {
        if (question.type === 'multiple_choice') {
          if (answers[question.id] === question.answer) {
            totalScore += question.points;
            correctAnswers++;
          }
        } else if (question.type === 'multiple_select') {
          const userAnswers = answers[question.id] || [];
          const correctAnswerSet = new Set(question.answer);
          
          // 检查用户答案与正确答案的匹配度
          if (userAnswers.length === question.answer.length && 
              userAnswers.every(ans => correctAnswerSet.has(ans))) {
            totalScore += question.points;
            correctAnswers++;
          }
        } else if (question.type === 'coding') {
          // 简单模拟代码评分（实际中应通过后端执行代码并比对结果）
          if (answers[question.id] && answers[question.id].length > question.initialCode.length) {
            // 假设代码长度超过初始代码，给一半的分数
            totalScore += question.points / 2;
            correctAnswers += 0.5;
          }
        }
      });
      
      // 设置结果
      setResult({
        score: totalScore,
        totalQuestions: exercise.questions.length,
        correctAnswers: correctAnswers,
        passed: totalScore >= exercise.passingPoints
      });
      
      setExerciseSubmitted(true);
      setShowSubmitConfirm(false);
      setIsSubmitting(false);
      
      message.success('练习已提交');
    }, 2000);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>加载中...</div>;
  }

  if (exerciseSubmitted && result) {
    return (
      <Result
        status={result.passed ? "success" : "warning"}
        title={result.passed ? "恭喜你完成了练习!" : "练习已完成，再接再厉!"}
        subTitle={`得分: ${result.score}/${exercise.totalPoints}, 答对 ${result.correctAnswers}/${exercise.questions.length} 题`}
        extra={[
          <Button 
            type="primary" 
            key="exercises" 
            onClick={() => navigate('/student/exercises')}
          >
            返回练习列表
          </Button>,
          <Button key="again" onClick={() => {
            setExerciseSubmitted(false);
            setCurrentQuestion(0);
          }}>
            查看答题详情
          </Button>,
        ]}
      />
    );
  }

  const currentQuestionData = exercise.questions[currentQuestion];

  const renderQuestionContent = () => {
    switch (currentQuestionData.type) {
      case 'multiple_choice':
        return (
          <Radio.Group 
            onChange={(e) => handleAnswerChange(currentQuestionData.id, e.target.value)}
            value={answers[currentQuestionData.id]}
          >
            <Space direction="vertical">
              {currentQuestionData.options.map(option => (
                <Radio key={option.id} value={option.id}>
                  {option.id}. {option.text}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        );
      
      case 'multiple_select':
        return (
          <div>
            {currentQuestionData.options.map(option => (
              <div key={option.id} style={{ marginBottom: 8 }}>
                <Checkbox
                  checked={(answers[currentQuestionData.id] || []).includes(option.id)}
                  onChange={(e) => handleMultipleSelectChange(
                    currentQuestionData.id, 
                    option.id, 
                    e.target.checked
                  )}
                >
                  {option.id}. {option.text}
                </Checkbox>
              </div>
            ))}
          </div>
        );
      
      case 'coding':
        return (
          <div>
            <Paragraph>{currentQuestionData.description}</Paragraph>
            <div style={{ marginTop: 16 }}>
              <CodeEditor 
                initialCode={currentQuestionData.initialCode}
                language={currentQuestionData.language}
                height="400px"
                onSave={(code) => handleCodeChange(currentQuestionData.id, code)}
                onRun={(code) => {
                  message.info('代码执行功能仅作展示，实际判题将在提交后进行');
                  handleCodeChange(currentQuestionData.id, code);
                }}
              />
            </div>
          </div>
        );
      
      default:
        return <div>不支持的题目类型</div>;
    }
  };

  const getQuestionTypeText = (type) => {
    const typeMap = {
      'multiple_choice': '单选题',
      'multiple_select': '多选题',
      'coding': '编程题',
    };
    return typeMap[type] || '未知类型';
  };

  const getQuestionIcon = (type) => {
    const iconMap = {
      'multiple_choice': <QuestionCircleOutlined />,
      'multiple_select': <QuestionCircleOutlined />,
      'coding': <CodeOutlined />,
    };
    return iconMap[type] || <FileTextOutlined />;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Button icon={<LeftOutlined />} onClick={() => navigate('/student/exercises')}>
          返回练习列表
        </Button>
        
        <div>
          <Tooltip title="剩余时间">
            <Tag color={timeLeft < 300 ? 'red' : timeLeft < 600 ? 'orange' : 'blue'} style={{ marginRight: 16 }}>
              <ClockCircleOutlined /> 剩余: {formatTime(timeLeft)}
            </Tag>
          </Tooltip>
          
          <Button 
            type="primary"
            onClick={() => setShowSubmitConfirm(true)}
          >
            提交练习
          </Button>
        </div>
      </div>
      
      <Card title={exercise.title} style={{ marginBottom: 24 }}>
        <Paragraph>{exercise.description}</Paragraph>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic title="题目数量" value={exercise.questions.length} />
          </Col>
          <Col span={8}>
            <Statistic title="总分值" value={exercise.totalPoints} />
          </Col>
          <Col span={8}>
            <Statistic title="及格分数" value={exercise.passingPoints} />
          </Col>
        </Row>
      </Card>
      
      <Card>
        <Steps current={currentQuestion} style={{ marginBottom: 24 }}>
          {exercise.questions.map((q, index) => (
            <Step 
              key={q.id} 
              title={getQuestionTypeText(q.type)} 
              icon={getQuestionIcon(q.type)}
              description={`${q.points}分`}
              onClick={() => goToQuestion(index)}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </Steps>
        
        <Card 
          title={
            <div>
              <Tag color="blue">{getQuestionTypeText(currentQuestionData.type)}</Tag>
              {`${currentQuestion + 1}. ${currentQuestionData.title}`}
              <Tag color="green" style={{ marginLeft: 8 }}>{currentQuestionData.points}分</Tag>
            </div>
          }
        >
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="题目描述" key="description">
              {renderQuestionContent()}
            </TabPane>
            {currentQuestionData.type === 'coding' && (
              <TabPane tab="测试用例" key="testCases">
                <div>
                  <Alert 
                    message="测试用例仅供参考，实际评分可能包含更多用例" 
                    type="info" 
                    showIcon 
                    style={{ marginBottom: 16 }}
                  />
                  {currentQuestionData.testCases.map((testCase, index) => (
                    <Card 
                      size="small" 
                      title={`测试用例 ${index + 1}`} 
                      style={{ marginBottom: 8 }}
                      key={index}
                    >
                      <div>
                        <strong>输入:</strong> {testCase.input.length ? testCase.input.join(', ') : '无输入参数'}
                      </div>
                      <div>
                        <strong>预期输出:</strong> {testCase.output.toString()}
                      </div>
                    </Card>
                  ))}
                </div>
              </TabPane>
            )}
          </Tabs>
        </Card>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
          <Button 
            type="default" 
            onClick={() => goToQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0}
          >
            <LeftOutlined /> 上一题
          </Button>
          
          {currentQuestion < exercise.questions.length - 1 ? (
            <Button 
              type="primary" 
              onClick={() => goToQuestion(currentQuestion + 1)}
            >
              下一题 <RightOutlined />
            </Button>
          ) : (
            <Button 
              type="primary" 
              onClick={() => setShowSubmitConfirm(true)}
            >
              完成练习 <CheckOutlined />
            </Button>
          )}
        </div>
      </Card>
      
      <Modal
        title="确认提交"
        open={showSubmitConfirm}
        onOk={submitExercise}
        onCancel={() => setShowSubmitConfirm(false)}
        confirmLoading={isSubmitting}
        okText="确认提交"
        cancelText="取消"
      >
        <p>您确定要提交此练习吗？提交后将不能再修改答案。</p>
        <p>请确保已完成所有题目。</p>
        <Alert 
          message={`您已完成 ${
            Object.values(answers).filter(a => a !== null && a !== undefined && (
              !Array.isArray(a) || a.length > 0
            )).length
          } 道题目，共 ${exercise.questions.length} 题。`} 
          type="info" 
          showIcon 
        />
      </Modal>
    </div>
  );
};

export default ExerciseDetail;  
